import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';


// INERFAZ PARA LA MATRIZ DE OPCIONES DEL AUTOCOMPLETE
export interface SelectOption {
  value: string;
  label: string;
  icon?: string;
  badge?: string;
  disabled?: boolean;
  isLoaded?: boolean;
  originalData?: any;
}

// INERFAZ PARA LAS TABS DEL AUTOCOMPLETE
export interface SelectTab {
  id: string;
  label: string;
  icon?: string;
  options: SelectOption[];
  count?: number;
}

// INTERFAZ PARA LA INFO QUE DEVUELVE EL STORE DE PAGINACION DE LA API
export interface PaginationInfo {
  total_registros: number;
  pagina_actual: number;
  tamano_pagina: number;
  total_paginas: number;
  tiene_pagina_anterior: boolean;
  tiene_pagina_siguiente: boolean;
}


// INTERFAZ PARA LA RESPUESTA DE LA API
export interface SearchResponse {
  pagination: PaginationInfo;
  data: SelectOption[];
}

@Component({
  selector: 'app-autocomplete-con-filtros',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './autocomplete-con-filtros.component.html',
  styleUrl: './autocomplete-con-filtros.component.scss'
})
export class AutocompleteConFiltrosComponent implements OnInit, OnDestroy {
  @Input() options: SelectOption[] = [];
  @Input() tabs: SelectTab[] = [];
  @Input() placeholder: string = 'Seleccionar opción';
  @Input() value: string = '';
  @Input() disabled: boolean = false;
  @Input() searchable: boolean = true;
  @Input() clearable: boolean = true;
  @Input() maxHeight: string = '300px';
  @Input() showTabs: boolean = false;
  @Input() icon: string = '';
  // Propiedades para búsqueda dinámica
  @Input() dynamicSearch: boolean = false;
  @Input() minSearchLength: number = 1;
  @Input() showInitialData: boolean = false;
  @Output() onOpen = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();


  @Input() debounceTime: number = 300;
  @Input() searchFunction?: (searchTerm: string, page?: number, pageSize?: number) => Promise<SearchResponse>;
  @Input() obtenerInfoLoaded?: (data: any) => SelectOption | null;
  @Input() dataOriginal?: any;
  @Input() initialPageSize: number = 50;
  @Input() allowCreate: boolean = false; // Permite crear nuevas opciones cuando no se encuentra coincidencia
  @Output() valueChange = new EventEmitter<string>();

  @Output() selectionChange = new EventEmitter<SelectOption>();
  @Output() onCreateNew = new EventEmitter<string>(); // Emite cuando se quiere crear una nueva opción
  @ViewChild('optionsContainer') optionsContainer!: ElementRef;
  @Output() open = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  isOpen = false;
  searchTerm = '';
  // OPCIONES FILTRADAS PARA MOSTRAR EN EL AUTOCOMPLETE
  filteredOptions: SelectOption[] = [];
  // OPCION SELECCIONADA
  selectedOption: SelectOption | null = null;
  // ID DE LA TAB ACTIVA
  activeTabId: string = '';
  // OPCIONES TOTALES
  allOptions: SelectOption[] = [];
  
  // Propiedades para búsqueda dinámica con paginación
  isLoading = false;
  isLoadingMore = false;
  paginationInfo: PaginationInfo | null = null;
  currentPage = 1;
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.initializeOptions();
    this.updateSelectedOption();
    this.setupSearchDebounce();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleLoadedItem() {
    if (this.selectedOption) {
      // Agregar a las opciones filtradas si no existe
      if (!this.filteredOptions.find(opt => opt.value === this.selectedOption?.value)) {
        this.filteredOptions = [this.selectedOption, ...this.filteredOptions];
      }

      // Seleccionar automáticamente si no hay valor seleccionado
      if (!this.value) {
        this.selectOption(this.selectedOption);
      }
    }
  }


  private setupSearchDebounce() {
    // EL SUBJET OBTIENE LOS CAMBIOS DEL INPUT DE BUSQUEDA Y LOS EMITE
    this.searchSubject
      .pipe(
        // DEBOUNCE TIME: ESPERA 300ms para emitir el cambio (PARA EVITAR PETICIONES EXCESIVAS)
        debounceTime(this.debounceTime),
        // DISTINCT UNTIL CHANGED: EMITE EL CAMBIO SOLO SI ES DISTINTO AL ULTIMO CAMBIO
        distinctUntilChanged(),
        // TAKE UNTIL: EMITE EL CAMBIO SOLO SI EL COMPONENTE ESTA ACTIVO
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        // REALIZA LA BUSQUEDA CON EL TERMINO DE BUSQUEDA Y LA PAGINA ACTUAL
        this.performSearch(searchTerm, this.currentPage);
      });
  }


  private async performSearch(searchTerm: string, page: number = 1) {
    // SI EL TERMINO DE BUSQUEDA ES VACIO O ES MENOR A LA LONGITUD MINIMA, LIMPIA LAS OPCIONES Y LA PAGINACION
    if (searchTerm.length < this.minSearchLength) {
      this.filteredOptions = [];
      this.paginationInfo = null;
      return;
    }

    // LOS LOADING SON DIFERENTES PARA MOSTRAR DIFERENTES ESQUELETOS DE CARGANDO
    if (page === 1) {
      this.isLoading = true;
    } else {
      this.isLoadingMore = true;
    }
    try {
      if (this.searchFunction && typeof this.searchFunction === 'function') {
        // LLAMA A LA FUNCION DE BUSQUEDA QUE ESTA EN EL COMPONENTE PADRE
        const response = await this.searchFunction(searchTerm, page, this.initialPageSize);
        // MANEJA LA RESPUESTA DE LA API
        this.handleSearchResponse(response, page);
      } 
    } catch (error) {
      console.error('Error en búsqueda:', error);
      if (page === 1) {
        this.filteredOptions = [];
        this.paginationInfo = null;
      }
    } finally {
      this.isLoading = false;
      this.isLoadingMore = false;
    }
  }

  private handleSearchResponse(response: SearchResponse, page: number) {
    this.paginationInfo = response.pagination;
    this.currentPage = page;
    
    if (page === 1) {
      // Primera página - reemplazar opciones
      this.filteredOptions = [...response.data];
    } else {
      // Páginas siguientes - agregar opciones
      this.filteredOptions = [...this.filteredOptions, ...response.data];
    }
  }

  // Método público para actualizar opciones desde el componente padre
  updateOptions(newOptions: SelectOption[], pagination?: PaginationInfo) {
    if (pagination) {
      this.paginationInfo = pagination;
      this.currentPage = pagination.pagina_actual;
      
      if (pagination.pagina_actual === 1) {
        this.filteredOptions = [...newOptions];
      } else {
        this.filteredOptions = [...this.filteredOptions, ...newOptions];
      }
    } else {
      this.filteredOptions = [...newOptions];
    }
  }

  // METODO PARA CARGAR MAS OPCIONES SE EJECUTA EN EL SCROLL
  loadMoreOptions() {
    if (this.isLoadingMore || !this.paginationInfo?.tiene_pagina_siguiente) {
      return;
    }
    const nextPage = this.currentPage + 1;
    this.performSearch(this.searchTerm, nextPage);
  }

  // Detectar scroll para cargar más opciones
  onOptionsScroll(event: Event) {
    const element = event.target as HTMLElement;
    const scrollPosition = element.scrollTop + element.clientHeight;
    const scrollHeight = element.scrollHeight;
    
    // Cargar más cuando esté cerca del final (80% del scroll)
    if (scrollPosition >= scrollHeight * 0.8) {
      this.loadMoreOptions();
    }
  }

  private initializeOptions() {
    if (this.dynamicSearch ) {
      // Modo búsqueda dinámica - no cargar opciones inicialmente por defecto
      this.allOptions = [];
      this.filteredOptions = [];
    } else if (this.showTabs && this.tabs.length > 0) {
      // Usar tabs si están disponibles y setea las opciones segun las tabs
      this.allOptions = this.tabs.flatMap(tab => tab.options);
      this.activeTabId = this.tabs[0].id;
      this.filteredOptions = this.getActiveTabOptions();
    } else {
      // Usar options tradicionales
      this.allOptions = [...this.options];
      this.filteredOptions = [...this.options];
    }
  }

  private getActiveTabOptions(): SelectOption[] {
    const activeTab = this.tabs.find(tab => tab.id === this.activeTabId);
    return activeTab ? activeTab.options : [];
  }

  @HostListener('document:click', ['$event'])
  // SI EL USUARIO HACE CLICK FUERA DEL AUTOCOMPLETE, CIERRA EL DROPDOWN
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.github-select')) {
      this.closeDropdown();
    }
  }

  // METODO PARA ABRI Y CERRAR EL DROPDOWN
  toggleDropdown(event: Event) {
    event.stopPropagation();
    if (this.disabled) return;
    
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchTerm = '';
      this.currentPage = 1;
      this.paginationInfo = null;
      this.open.emit();
      if (!this.dynamicSearch) {
        this.filterOptions();
      } else if (this.showInitialData) {
        this.performInitialSearch();
      }
    } else {
      this.close.emit();
    }
    if (this.isOpen) {
      this.onOpen.emit();
    } else {
      this.onClose.emit();
    }   
  }

  private async performInitialSearch() {
    if (!this.searchFunction) return;
    
    this.isLoading = true;
    try {
      // Realizar búsqueda con término vacío para obtener datos iniciales
      const response = await this.searchFunction('', 1, this.initialPageSize);
      this.handleSearchResponse(response, 1);
    } catch (error) {
      console.error('Error en búsqueda inicial:', error);
      this.filteredOptions = [];
      this.paginationInfo = null;
    } finally {
      this.isLoading = false;
    }
  }

  // METODO PARA CERRAR EL DROPDOWN
  closeDropdown() {
    this.isOpen = false;
    this.searchTerm = '';
    this.currentPage = 1;
    this.paginationInfo = null;
    this.close.emit();
    if (!this.dynamicSearch) {
      this.filterOptions();
    }
    this.onClose.emit();
  }

  // METODO PARA FILTRAR LAS OPCIONES SE EJECUTA EN EL INPUT DE BUSQUEDA
  onSearchInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    // DEPENDE LA BUSQUEDA CONSULTA LA API O FILTRA LAS OPCIONES
    if (this.dynamicSearch) {
      // En modo dinámico, usar debounce y resetear paginación
      this.currentPage = 1;
      this.paginationInfo = null;
      this.searchSubject.next(this.searchTerm);
    } else {
      // En modo estático, filtrar inmediatamente
      this.filterOptions();
    }
  }

  // METODO PARA FILTRAR LAS OPCIONES SE EJECUTA EN EL INPUT DE BUSQUEDA CUANDO LA BUSQUEDA NO ES DINAMICA
  filterOptions() {
    if (this.showTabs && this.tabs.length > 0) {
      // Filtrar en todas las pestañas cuando hay búsqueda
      if (!this.searchTerm?.trim()) {
        this.filteredOptions = this.getActiveTabOptions();
      } else {
        this.filteredOptions = this.allOptions.filter(option =>
          option.label.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          option.value.toLowerCase().includes(this.searchTerm.toLowerCase())
        );
      }
    } else {
      // Filtrado tradicional
      if (!this.searchTerm?.trim()) {
        this.filteredOptions = [...this.options];
      } else {
        this.filteredOptions = this.options.filter(option =>
          option.label.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          option.value.toLowerCase().includes(this.searchTerm.toLowerCase())
        );
      }
    }
  }

  // METODO PARA SELECCIONAR UNA OPCION
  selectOption(option: SelectOption) {
    if (option.disabled) return;
    
    // Si la opción es especial para crear nueva (value empieza con '__CREATE__')
    if (option.value.startsWith('__CREATE__')) {
      const newValue = option.value.replace('__CREATE__', '');
      this.onCreateNew.emit(newValue);
      // Crear una nueva opción con el valor ingresado
      const newOption: SelectOption = {
        value: newValue,
        label: newValue
      };
      this.value = newOption.value;
      this.selectedOption = newOption;
      this.valueChange.emit(this.value);
      this.selectionChange.emit(newOption);
      this.closeDropdown();
      return;
    }
    
    this.value = option.value;
    this.selectedOption = option;
    this.valueChange.emit(this.value);
    this.selectionChange.emit(option);
    this.closeDropdown();
  }

  // METODO PARA LIMPIAR LA OPCION SELECCIONADA
  clearSelection(event: Event) {
    event.stopPropagation();
    this.value = '';
    this.selectedOption = null;
    this.valueChange.emit('');
    this.selectionChange.emit(null as any);
  }

  // METODO PARA CAMBIAR DE TAB
  switchTab(tabId: string) {
    this.activeTabId = tabId;
    if (!this.searchTerm?.trim()) {
      this.filteredOptions = this.getActiveTabOptions();
    }
  }

  // Método para actualizar la opción seleccionada
  updateSelectedOption() {
    if(this.allOptions.length > 0){
      this.selectedOption = this.allOptions.find(option => option.value === this.value) || null;
    }

    const infoLoaded = this.obtenerInfoLoaded ? this.obtenerInfoLoaded(this.dataOriginal) : null;
    if(infoLoaded){
      this.selectedOption = infoLoaded;
      this.handleLoadedItem();
    }    
  }

  // METODO PARA MOSTRAR EL VALOR SELECCIONADO
  get displayValue(): string {
    return this.selectedOption?.label || this.placeholder;
  }

  // METODO PARA VERIFICAR SI DEBE MOSTRAR LA OPCION DE CREAR NUEVA
  get shouldShowCreateOption(): boolean {
    if (!this.allowCreate || !this.searchTerm || this.searchTerm.trim() === '') {
      return false;
    }
    
    // No mostrar si está cargando
    if (this.isLoading || this.isLoadingMore) {
      return false;
    }
    
    // Verificar si el término de búsqueda no coincide exactamente con ninguna opción
    const searchTermLower = this.searchTerm.toLowerCase().trim();
    const hasExactMatch = this.filteredOptions.some(option => 
      option.label.toLowerCase() === searchTermLower || 
      option.value.toLowerCase() === searchTermLower
    );
    
    // Solo mostrar si no hay coincidencia exacta y el término tiene al menos un carácter
    return !hasExactMatch && searchTermLower.length > 0;
  }

  // METODO PARA OBTENER LA OPCION DE CREAR NUEVA
  get createNewOption(): SelectOption {
    return {
      value: `__CREATE__${this.searchTerm.trim()}`,
      label: `Crear "${this.searchTerm.trim()}"`,
      icon: 'M12 4v16m8-8H4'
    };
  }

  // METODO PARA VER SI EL VALOR SELECCIONADO ES DISTINTO DE VACIO
  get hasValue(): boolean {
    return this.value && this.value?.toString().trim() !== '' ? true : false;
  }

  // METODO PARA MOSTRAR LA TAB ACTIVA
  get activeTab(): SelectTab | undefined {
    return this.tabs.find(tab => tab.id === this.activeTabId);
  }

  // METODO PARA TRACKING DE LAS OPCIONES
  trackByValue(index: number, option: SelectOption): string {
    return option.value;
  }

  // METODO PARA TRACKING DE LAS TABS
  trackByTabId(index: number, tab: SelectTab): string {
    return tab.id;
  }

  // METODO PARA CALCULAR LA ALTURA DE LA LISTA DE OPCIONES
  getOptionsListHeight(): string {
    let height = this.maxHeight;
    
    if (this.searchable) {
      height = `calc(${this.maxHeight} - 60px)`;
    }
    
    if (this.showTabs && this.tabs.length > 0 && !this.searchTerm?.trim()) {
      height = `calc(${height} - 48px)`; // Altura de las pestañas
    }
    
    return height;
  }

  // METODO PARA GENERAR ESQUELETOS DE CARGANDO
  getSkeletonItems(): number[] {
    return Array.from({ length: 5 }, (_, i) => i);
  }
}